import React from "react";
import { Box, Grid2, useTheme } from "@mui/material";
import MarkEmailUnreadOutlinedIcon from "@mui/icons-material/MarkEmailUnreadOutlined";
import PriorityHighRoundedIcon from "@mui/icons-material/PriorityHighRounded";
import PendingActionsOutlinedIcon from "@mui/icons-material/PendingActionsOutlined";
import { observer } from "mobx-react-lite";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import { alertsStore } from "../../../stores/alerts/AlertsStore";
import { AlertListModel } from "../../../stores/alerts/AlertListModel";
import { getAlertColors, getSeverityTone } from "./alertUi";
import {
  AlertsStatTile,
  trackAlertsAction,
} from "../../../../../analytics/alertsTracking";

interface AlertStatTilesProps {
  list: AlertListModel;
}

/** Badge counts for the current section; each tile applies its filter. */
const AlertStatTiles: React.FC<AlertStatTilesProps> = ({ list }) => {
  const theme = useTheme();
  const colors = getAlertColors(theme);
  const high = getSeverityTone(theme, "HIGH");
  const medium = getSeverityTone(theme, "MEDIUM");

  const row = list.section
    ? alertsStore.sections.find((section) => section.code === list.section)
    : null;
  const unread = row ? row.unread : alertsStore.totalUnread;
  const highUnread = row ? row.high_unread : alertsStore.totalHighUnread;
  const open = row
    ? row.open
    : alertsStore.sections.reduce((sum, section) => sum + section.open, 0);

  const clickTile = (
    tile: AlertsStatTile,
    filters: Parameters<AlertListModel["setFilters"]>[0],
  ) => {
    trackAlertsAction("stat_tile_click", "inbox", {
      view: list.view,
      section: list.section,
      severityFilter: list.severity,
      tile,
    });
    list.setFilters(filters);
  };

  const tiles = [
    {
      key: "unread",
      label: "Unread",
      value: unread,
      hint: "Waiting for you",
      fg: colors.accent,
      icon: <MarkEmailUnreadOutlinedIcon sx={{ fontSize: 20 }} />,
      active: list.view === "UNREAD" && list.severity == null,
      onClick: () => clickTile("unread", { view: "UNREAD", severity: null }),
    },
    {
      key: "high",
      label: "High priority",
      value: highUnread,
      hint: "Unread, high severity",
      fg: high.fg,
      icon: <PriorityHighRoundedIcon sx={{ fontSize: 20 }} />,
      active: list.view === "UNREAD" && list.severity === "HIGH",
      onClick: () => clickTile("high", { view: "UNREAD", severity: "HIGH" }),
    },
    {
      key: "open",
      label: "Open",
      value: open,
      hint: "Need resolving",
      fg: medium.fg,
      icon: <PendingActionsOutlinedIcon sx={{ fontSize: 20 }} />,
      active: list.view === "OPEN" && list.severity == null,
      onClick: () => clickTile("open", { view: "OPEN", severity: null }),
    },
  ];

  return (
    <Grid2 container spacing={1.5}>
      {tiles.map((tile) => (
        <Grid2 key={tile.key} size={{ xs: 12, sm: 4 }}>
          <Box
            component="button"
            type="button"
            onClick={tile.onClick}
            sx={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              p: 1.75,
              cursor: "pointer",
              textAlign: "left",
              fontFamily: "inherit",
              borderRadius: "12px",
              bgcolor: colors.surface,
              border: `1px solid ${tile.active ? tile.fg : colors.border}`,
              boxShadow: tile.active
                ? `0 0 0 3px ${tile.fg}1A`
                : `0 2px 8px ${theme.palette.customColors.shadowVeryLight}`,
              transition: "all 0.15s ease",
              "&:hover": { borderColor: tile.fg },
            }}
          >
            <Box
              sx={{
                width: 42,
                height: 42,
                borderRadius: "12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                color: tile.fg,
                bgcolor: `${tile.fg}14`,
              }}
            >
              {tile.icon}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <CustomTypography variant="heading8" color={colors.subtitle}>
                {tile.label}
              </CustomTypography>
              <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
                <CustomTypography
                  variant="heading3"
                  color={
                    tile.value > 0 && tile.key === "high"
                      ? tile.fg
                      : colors.title
                  }
                  sx={{ mt: 0.25, fontVariantNumeric: "tabular-nums" }}
                >
                  {alertsStore.summary ? tile.value : "–"}
                </CustomTypography>
                <CustomTypography variant="caption" color={colors.muted}>
                  {tile.hint}
                </CustomTypography>
              </Box>
            </Box>
          </Box>
        </Grid2>
      ))}
    </Grid2>
  );
};

export default observer(AlertStatTiles);
